// src/components/ServiceTypeForm.tsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Error403Page from "./Error_backend";
import api from "../api.js";
import "../style/css/AppointmentForm.css";
import DeleteModal from "./DeleteModal";

interface ServiceTypeFormData {
  name: string;
  description: string;
}

const ServiceTypeForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<ServiceTypeFormData>({
    name: "",
    description: "",
  });

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // API base URL - using /services endpoint as specified
  const API_BASE = "/service-types";

  // Validate form data
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields validation
    if (!formData.name.trim()) {
      errors.name = "El nombre del tipo de servicio es requerido";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Fetch service type data if in edit mode
  useEffect(() => {
    const fetchServiceType = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`${API_BASE}/${id}/`);
        if (!response) {
          throw new Error("Failed to fetch service type");
        }

        const serviceTypeData = await response.data;

        // Format the data for the form
        setFormData({
          name: serviceTypeData.name || "",
          description: serviceTypeData.description || "",
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchServiceType();
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      setError("Por favor, corrija los errores en el formulario");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let response;
      if (isEditMode) {
        response = await api.put(`${API_BASE}/${id}/`, formData);
      } else {
        response = await api.post(`${API_BASE}/`, formData);
      }

      if (!response) {
        throw new Error(
          `Failed to ${isEditMode ? "update" : "create"} service type`
        );
      }

      navigate("/services?tab=service-types");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    setError(null);

    try {
      const response = await api.delete(`${API_BASE}/${id}/`);

      if (!response) {
        throw new Error("Error al eliminar el tipo de servicio");
      }

      navigate("/services?tab=service-types");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ocurrió un error");
    } finally {
      setDeleting(false);
    }
  };

  const handleBackClick = () => {
    navigate("/services?tab=service-types"); // Adjust to your service types list route
  };

  // Show loading state
  if (loading) {
    return (
      <div className="form-entire-container-form-appointment">
        <div className="form-container-form-appointment">
          <div className="loading-message-form-appointment">
            <i className="fas fa-spinner fa-spin"></i> Cargando tipo de
            servicio...
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && isEditMode) {
    return (
      <Error403Page
        code={500}
        title="Recurso no disponible."
        message="Hemos tenido problemas para acceder al recurso."
        homeHref="/"
        loginHref="/login"
        supportHref="/support"
        onHomeClick={(e) => {
          e.preventDefault();
          navigate("/");
        }}
        onLoginClick={(e) => {
          e.preventDefault();
          navigate("/login");
        }}
      />
    );
  }

  const titleText = isEditMode
    ? "Editar Tipo de Servicio"
    : "Crear Nuevo Tipo de Servicio";

  return (
    <div className="form-entire-container-form-appointment">
      <div className="form-container-form-appointment">
        <div className="header-actions-form-appointment">
          <button
            className="back-link-form-appointment"
            onClick={handleBackClick}
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <i className="fas fa-arrow-left"></i> Volver a Tipos de Servicio
          </button>
          <h2 style={{ margin: 0, fontSize: "1.5rem" }}>
            <i
              className="fas fa-tags"
              style={{ color: "var(--color-accent)" }}
            ></i>{" "}
            {titleText}
          </h2>
          <span></span>
        </div>

        <div className="form-card-form-appointment">
          <h3 style={{ marginBottom: "1.5rem", textAlign: "center" }}>
            {isEditMode
              ? `Editando: ${formData.name}`
              : "Nuevo tipo de servicio"}
          </h3>

          {error && (
            <div className="message-box-form-appointment message-error-form-appointment">
              <i className="fas fa-exclamation-triangle"></i>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <div className="form-group-form-appointment">
              <label htmlFor="name">Nombre del Tipo de Servicio *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={saving || deleting}
                placeholder="Ej: Consulta, Terapia, Cirugía, Diagnóstico..."
                className={formErrors.name ? "input-error" : ""}
              />
              {formErrors.name && (
                <span className="field-error">{formErrors.name}</span>
              )}
            </div>

            <div className="form-group-form-appointment">
              <label htmlFor="description">Descripción</label>
              <textarea
                id="description"
                name="description"
                rows={4}
                placeholder="Ej: Consultas médicas generales para evaluación inicial y seguimiento de pacientes..."
                value={formData.description}
                onChange={handleChange}
                disabled={saving || deleting}
              ></textarea>
            </div>

            <hr
              style={{
                borderColor: "var(--neutral-200)",
                margin: "var(--space-md) 0",
              }}
            />

            <div className="action-footer-form-appointment">
              {isEditMode && (
                <div>
                  <button
                    type="button"
                    className="btn--delete-form-appointment"
                    onClick={() => setIsDeleteModalOpen(true)}
                    disabled={saving || deleting}
                  >
                    <i className="fas fa-trash-alt"></i>
                    {deleting ? "Eliminando..." : "Eliminar Tipo de Servicio"}
                  </button>
                  <DeleteModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onConfirm={handleDeleteConfirm}
                    title="¿Eliminar tipo de servicio?"
                    description="Esto eliminará el tipo de servicio de forma permanente."
                    confirmText="Eliminar tipo de servicio"
                    cancelText="Mantener tipo de servicio"
                  />
                </div>
              )}
              <button
                type="submit"
                className={`btn btn--primary btn--large btn--save-form-appointment ${
                  !isEditMode ? "btn--full-width-in-create" : ""
                }`}
                disabled={saving || deleting}
              >
                <i className="fas fa-save"></i>{" "}
                {saving
                  ? isEditMode
                    ? "Guardando..."
                    : "Creando..."
                  : isEditMode
                  ? "Guardar Cambios"
                  : "Crear Tipo de Servicio"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ServiceTypeForm;
